/**
 * The Eingangsbeleg (docs/18-vorratsbuch.md, Eingänge): one format for everything that comes into
 * the pantry, whatever brought it. Each channel has its own converter to this format (a delivery
 * note as PDF, a table, a purchase of the shopping list, later a photo of a receipt); after that
 * every Eingang goes the same way: lines mapped to articles, storage and best-before chosen, booked
 * as one Beleg. A converter only reads, it decides nothing: what is written stays as written
 * (`text`, `qty`, `unit`), the parts it understood come next to it.
 */

/** Where the Eingang comes from. */
export type InboundChannel = "pdf" | "table" | "shopping-list" | "hand";

/** Base units of the pantry (unit model B). */
export type BaseUnit = "g" | "ml" | "Stk.";

export interface Amount {
  qty: number;
  unit: BaseUnit;
}

export interface InboundLine {
  /** Position in the document, from 1. */
  line: number;
  /** The name as written: „Birnen Conference“, „Ganzkornbrot 1 kg“. */
  text: string;
  /** The name without a content in it: „Ganzkornbrot“, „Bioland Eier L“. */
  name: string;
  /** Quantity and unit as written: 0,466 „kg“; 1 „Stück“. */
  qty: number;
  unit: string;
  /** Containers, when the unit counts them („1 Stück“ Ganzkornbrot 1 kg); null for loose goods by weight. */
  packs: number | null;
  /** Content of one container, from the name („1 kg“, „6 Eier“); null if the name gives none. */
  content: Amount | null;
  /** All of it in the base unit (466 g; 1000 g; 6 Stk.); null if the unit says nothing about it („1 Bund“). */
  total: Amount | null;
  /** Cents for the whole line, after discount; the amount of the receipt. */
  amount?: number;
  /** Cents per unit as written, before discount. */
  unitPrice?: number;
  /** Percent. */
  vat?: number;
  discount?: number;
  /** Country code as written: „DE“, „I“, „EC“ (Ecuador). */
  origin?: string;
  /** Organic label as written: „Demeter“, „Bioland“, „EG-Bio“. */
  label?: string;
  organic?: boolean;
  /** What was ordered instead: „Kopfsalat grün“ was replaced by this line. */
  replaces?: string;
  /** The box or order it belongs to: „Single-Kiste M“, „Individualbestellung“. */
  group?: string;
  /** Only from a table or a scan. */
  barcode?: string;
  bestBefore?: string;
  /** Storage or location as written in a table. */
  place?: string;
  /** Master data a table gives for a new article: brand, base product, container, restock rule, barcodes. */
  fields?: Record<string, unknown>;
}

export interface Inbound {
  channel: InboundChannel;
  /** The converter: „dirks-biokiste“. */
  format: string;
  /** A delivery brings goods (invoice, delivery note, purchase); an order announces them. */
  kind: "delivery" | "order";
  /** „Dirks Bio-Kiste“. */
  supplier?: string;
  /** The document's number: „D21429689“. */
  ref?: string;
  /** Recognises the same document a second time, so nothing is booked twice. */
  key: string;
  /** Day of delivery, „2026-02-20“. */
  date?: string;
  /** Day of the document. */
  issued?: string;
  lines: InboundLine[];
  /** Cents: delivery fee, sum of the goods, sum to pay; as printed. */
  fee?: number;
  goods?: number;
  total?: number;
  /** What the converter noticed: a sum that does not add up, a row it did not understand. */
  notes: string[];
}

const UNITS: Record<string, { unit: BaseUnit; factor: number } | "pack"> = {
  kg: { unit: "g", factor: 1000 },
  g: { unit: "g", factor: 1 },
  l: { unit: "ml", factor: 1000 },
  ml: { unit: "ml", factor: 1 },
  stück: "pack",
  stk: "pack",
  st: "pack",
  bund: "pack",
  schale: "pack",
  glas: "pack",
  packung: "pack",
  pck: "pack",
  dose: "pack",
  flasche: "pack",
  netz: "pack",
  beutel: "pack",
  kiste: "pack",
};

/** A unit as written, understood: by weight or volume, or counting containers; null if unknown. */
export function inboundUnit(unit: string): { unit: BaseUnit; factor: number } | "pack" | null {
  return UNITS[unit.toLowerCase().replace(/\.$/, "")] ?? null;
}

/** A content in a name: „Ganzkornbrot 1 kg“ → 1000 g, „6 Bioland Eier L“ → 6 Stk.; with the name without it. */
export function contentOf(text: string): { name: string; content: Amount | null } {
  const m = /(?:^|\s)(\d+(?:,\d+)?)\s*(kg|g|l|ml)(?=$|[\s,)])/i.exec(text);
  if (m) {
    const u = inboundUnit(m[2]!);
    if (u && u !== "pack") {
      const name = (text.slice(0, m.index) + text.slice(m.index + m[0].length)).replace(/\s+/g, " ").trim();
      return { name, content: { qty: Math.round(Number(m[1]!.replace(",", ".")) * u.factor), unit: u.unit } };
    }
  }
  const lead = /^(\d+)\s+(?=\D)/.exec(text);
  if (lead) return { name: text.slice(lead[0].length).replace(/\s+/g, " ").trim(), content: { qty: Number(lead[1]), unit: "Stk." } };
  return { name: text.replace(/\s+/g, " ").trim(), content: null };
}

/** The quantity parts of a line from what is written: 0,466 kg → 466 g; 1 Stück „Ganzkornbrot 1 kg“ → 1 pack of 1000 g. */
export function quantityOf(text: string, qty: number, unit: string): Pick<InboundLine, "name" | "packs" | "content" | "total"> {
  const { name, content } = contentOf(text);
  const u = inboundUnit(unit);
  if (u && u !== "pack") return { name, packs: null, content, total: { qty: Math.round(qty * u.factor), unit: u.unit } };
  if (u === "pack") {
    const total = content ? { qty: Math.round(qty * content.qty), unit: content.unit } : unit.toLowerCase().startsWith("st") ? { qty: Math.round(qty), unit: "Stk." as const } : null;
    return { name, packs: qty, content, total };
  }
  return { name, packs: null, content, total: null };
}
